import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    useCallback,
    useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { apiCall } from "../services/apiClient";

const ProspectosContext = createContext();

export const ProspectosProvider = ({ children }) => {
    const { user, isLoading: authLoading, activeProfile, activeCompany } = useAuth(); 
    const [prospectos, setProspectos] = useState([]);
    const [loadingProspectos, setLoadingProspectos] = useState(true);
    const [errorProspectos, setErrorProspectos] = useState(null);
    const isFetching = useRef(false);

    const profileRef = useRef(activeProfile);
    const companyRef = useRef(activeCompany);
    useEffect(() => { profileRef.current = activeProfile; }, [activeProfile]);
    useEffect(() => { companyRef.current = activeCompany; }, [activeCompany]);

    // 1. Manejo inteligente de limpieza (CORREGIDO)
    useEffect(() => {
        if (!authLoading && !user?.id) {
            setProspectos([]);
            setErrorProspectos(null);
            localStorage.removeItem("prospectos");
        }
    }, [user?.id, authLoading]);

    // 2. Función de carga de datos
    const fetchAllProspects = useCallback(async () => {
        if (!user?.id) return;

        isFetching.current = true;
        setLoadingProspectos(true);
        setErrorProspectos(null);

        try {
            const p = profileRef.current;
            const c = companyRef.current;

            if (!p?.id || !p?.profile_type) {
                return;
            }

            const dashboardParams = { 
                user_id: user.id,
                profile_type: p.profile_type 
            };
            if (p.profile_type === 'empresa') {
                dashboardParams.company_id = c?.id || p.id;
            } else {
                dashboardParams.profile_id = p.id;
            }

            const data = await apiCall("metrics", "dashboard", dashboardParams);

            if (data.success || data.status === "success") {
                const items = data.prospects || data.data || [];
                setProspectos(items);
            } else {
                throw new Error(data.message || "Error informado por el servidor");
            }

        } catch (error) {
            setErrorProspectos(error.message);
        } finally {
            setLoadingProspectos(false);
            isFetching.current = false;
        }
    }, [user?.id]);

    // 3. Función de eliminación
    const deleteProspectsFromList = useCallback(async (ids) => {
        try {
            await Promise.all(
                ids.map(async (id) => {
                    return apiCall("prospects", "delete", { id });
                })
            );

            setProspectos((prev) => prev.filter((p) => !ids.includes(p.id)));
            return { success: true };
        } catch (error) {
            throw error;
        }
    }, []);

    // 4. Limpiar datos obsoletos ANTES de que cambie el perfil (evita mostrar datos del perfil anterior)
    useEffect(() => {
        const handleProfileChanging = () => {
            setProspectos([]);
            setLoadingProspectos(true);
        };
        window.addEventListener("crm:profileChanging", handleProfileChanging);
        return () => window.removeEventListener("crm:profileChanging", handleProfileChanging);
    }, []);

    // 5. Refetch cuando cambia el perfil activo (empresa/profesional/principal)
    useEffect(() => {
        const handleProfileChange = () => {
            setProspectos([]);
            setLoadingProspectos(true);
            if (user?.id) {
                fetchAllProspects();
            }
        };

        window.addEventListener("crm:profileChanged", handleProfileChange);
        return () => window.removeEventListener("crm:profileChanged", handleProfileChange);
    }, [user?.id, fetchAllProspects]);

    const value = {
        prospectos,
        setProspectos, 
        loadingProspectos,
        errorProspectos,
        fetchProspectos: fetchAllProspects, 
        deleteProspectsFromList,
    };

    return (
        <ProspectosContext.Provider value={value}>
            {children}
        </ProspectosContext.Provider>
    );
};

export const useProspectos = () => {
    const context = useContext(ProspectosContext);
    if (!context) throw new Error("useProspectos debe estar dentro de ProspectosProvider");
    return context;
};